/**
 * Which grid a source can drive. Each of the two grids plays one role.
 */
export type GridRole = 'comparison' | 'base'

/**
 * A selectable data source (the thing a grid is pointed at). Prometheus is a single grid source
 * even though its rows originate from two feeds (Custody + Trading) — see {@link RowOrigin}.
 */
export type SourceKey = 'api' | 'prom' | 'cw'

/**
 * The true origin of a single row. Prometheus rows are either Custody or Trading; match counting
 * and the Source column distinguish them, so origins are finer-grained than {@link SourceKey}.
 */
export type RowOrigin = 'api' | 'cust' | 'trad' | 'cw'

/**
 * A source a row can be matched against. Same domain as {@link RowOrigin} — 'prom' is never a
 * match target because matches are counted against the concrete Custody/Trading feeds.
 */
export type MatchTargetKey = RowOrigin

export type SourceTag = 'API' | 'EXT' | 'CORE'

/**
 * The RPS source name each grid source maps to for the field-catalog API (`sources=` param).
 * Enfusion is the connected API feed, Prometheus the normalized Custody+Trading feed, Clearwater
 * the ABOR golden copy. Required by the async column pipeline (fetchCatalog uses
 * RPS_SOURCE[sourceKey]).
 */
export const RPS_SOURCE: Record<SourceKey, 'ENFUSION' | 'PROMETHEUS' | 'CW'> = {
  api: 'ENFUSION',
  prom: 'PROMETHEUS',
  cw: 'CW',
}

/**
 * Sources each grid role is allowed to display. Enfusion is the only source allowed in both roles,
 * which is what makes the auto-swap in {@link WorkbenchStoreService.setSource} necessary.
 */
export const ROLE_ALLOWED: Record<GridRole, SourceKey[]> = {
  comparison: ['prom', 'api'],
  base: ['cw', 'api'],
}

/** For a given grid source, which target feeds we show match-count columns for. */
export const MATCH_TARGETS: Record<SourceKey, MatchTargetKey[]> = {
  api: ['cust', 'trad', 'cw'],
  prom: ['api', 'cw'],
  cw: ['cust', 'trad', 'api'],
}

/**
 * The connected front-office PMS. Data-driven so wiring a future non-Enfusion API is a one-line
 * change here, not a UI relabel.
 */
export const API_SOURCE = { name: 'Enfusion', transport: 'API' } as const

/** Default source per role — Comparison = Prometheus, Base = Enfusion. */
export const DEFAULT_SOURCE: Record<GridRole, SourceKey> = {
  comparison: 'prom',
  base: 'api',
}

/**
 * The source a role falls back to when Enfusion is taken over by the other grid. Describes the
 * unrestricted pairing, so it stays correct once the `disabledRoles` held back in the source
 * registry are cleared; the swap itself cannot fire today because Comparison may not select
 * Enfusion.
 */
export const FALLBACK_SOURCE: Record<GridRole, SourceKey> = {
  comparison: 'prom',
  base: 'cw',
}

/** Collapse a row origin to the grid source it lives under (Custody/Trading -> Prometheus). */
export function gridKeyOf(origin: RowOrigin): SourceKey {
  return origin === 'cust' || origin === 'trad' ? 'prom' : origin
}

export interface TargetMeta {
  label: string
  color: string
}

/** Display metadata for each match target (label + accent colour). */
export const TARGET_META: Record<MatchTargetKey, TargetMeta> = {
  cust: { label: 'Custody', color: '#217dfe' },
  trad: { label: 'Trading', color: '#e48727' },
  api: { label: API_SOURCE.name, color: '#7d5fff' },
  cw: { label: 'Clearwater', color: '#0047d1' },
}

/**
 * The read API's `matchCounts`/`matchedIds` blob is keyed by these names rather than by
 * {@link MatchTargetKey} directly. Used as the server-reported fallback for a row's match counts
 * until a manual Match action gives the client-side graph something more current to show instead.
 */
export const MATCH_COUNTS_FIELD: Record<MatchTargetKey, string> = {
  cust: 'custodyMatches',
  trad: 'tradingMatches',
  api: 'enfusionMatches',
  cw: 'clearwaterMatches',
}

/** The other role — the grid a cross-grid action targets. */
export function otherRole(role: GridRole): GridRole {
  return role === 'comparison' ? 'base' : 'comparison'
}
